import { Patient, PatientAlreadyExistsFault, PatientUpdateCommand } from '@dreryk/egabinet-patient-frontend-api';
import { MedicalRecordNumber } from '@dreryk/egabinet-patient-published-language-api';

import { useCqrsCommand, UseCqrsCommandResult } from '@dreryk/command-query-react-common';
import { ActionBar, ButtonVariant, dispatchSnackbarPositiveEvent, Tooltip } from '@dreryk/react-components';

import { PersonalDataFormBean, PersonalDataFormController } from '../../common/common.types';
import { patientPersonalDataCommandTransformer } from '../../common/common.utils';
import { refetchPatientQuery } from '../../common/PatientForm.loader';
import { patientTranslationKeys } from '../../translations';
import { usePatientAlreadyExistsFaultHandler } from '../../common/common.hooks';

type PatientUpdateActionProps = Pick<PersonalDataFormController, 'handleSubmit'> & {
   readonly patientId: number;
   readonly disabled?: boolean;
   readonly patientMedicalRecordNumber: MedicalRecordNumber | null;
};

export const usePatientUpdateCommand = (
   patientId: number,
   patientMedicalRecordNumber: MedicalRecordNumber | null,
   onSuccess?: VoidFunction
): UseCqrsCommandResult<Patient, PersonalDataFormBean, PatientAlreadyExistsFault> => {
   const patientAlreadyExistsFaultHandler = usePatientAlreadyExistsFaultHandler();

   return useCqrsCommand(
      (parameters) => {
         const command = new PatientUpdateCommand();
         patientPersonalDataCommandTransformer(command, parameters, patientMedicalRecordNumber);
         command.id = patientId;
         return command;
      },
      {
         onSuccess: () => {
            onSuccess?.();
            dispatchSnackbarPositiveEvent(patientTranslationKeys.patientDataUpdated);
         },
         onFault: {
            'egabinet-patient-frontend-api:PatientAlreadyExistsFault': patientAlreadyExistsFaultHandler,
         },
      }
   );
};

export const PatientConfirmedUpdateAction = (props: PatientUpdateActionProps): JSX.Element => {
   const { executeAsync: updatePatient } = usePatientUpdateCommand(props.patientId, props.patientMedicalRecordNumber);

   const handleSubmit = props.handleSubmit(async (data: PersonalDataFormBean) => {
      await updatePatient(data);
      refetchPatientQuery();
   });

   return (
      <Tooltip content={patientTranslationKeys.dataHasBeenSaved} testId={'patientUpdate'} disabled={props.disabled}>
         <ActionBar.PrimaryButton
            testId={'save'}
            label={'common:save'}
            variant={ButtonVariant.PRIMARY}
            onClick={handleSubmit}
            disabled={props.disabled}
            blockingOperation
         />
      </Tooltip>
   );
};
